import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Layers,
  TreePine,
  Wrench,
  Sparkles,
  Check,
  FileDown,
} from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import UtilityBar from "../components/site/UtilityBar";
import QuoteDialog from "../components/site/QuoteDialog";
import CatalogDialog from "../components/site/CatalogDialog";
import AccessibilityPanel from "../components/site/AccessibilityPanel";
import MobileStickyBar from "../components/site/MobileStickyBar";
import ResponsiveImg from "../components/site/ResponsiveImg";
import SeoHead from "../components/site/SeoHead";
import FaqSection from "../components/site/FaqSection";
import BreadcrumbSchema from "../components/site/BreadcrumbSchema";

const CATEGORIES = [
  {
    code: "EAD · CAST ALUMINUM",
    title: "Cast Aluminum Doors",
    count: "24 Designs",
    desc:
      "Bold, textural, weather-proof. Ribbed, paneled, slatted, gold-accented, even LED-lit. Powder-coated aluminum, electronic locking, integrated handles.",
    img: "/viewlux/doors/cat-aluminum.jpg",
    Icon: Layers,
  },
  {
    code: "WOD · WOODEN",
    title: "Wooden Entry Doors",
    count: "18 Designs",
    desc:
      "Natural wood veneer, painted, or dyed-paper finishes. Solid core, semi-solid core, chipboard cores. Modern slatted, paneled, and grand double-door silhouettes.",
    img: "/viewlux/doors/cat-wood.jpg",
    Icon: TreePine,
  },
  {
    code: "SSD · STAINLESS STEEL",
    title: "Stainless Steel Doors",
    count: "12 Designs",
    desc:
      "Brushed or polished steel with aluminum honeycomb core. Ribbed textures, wood-accent insets, glass paneling, and gold-accent statement designs.",
    img: "/viewlux/doors/cat-stainless.jpg",
    Icon: Wrench,
  },
  {
    code: "INT · INTERIOR",
    title: "Interior Door Series",
    count: "70+ Designs",
    desc:
      "Painted, Slat, Carved, High Gloss, Flat, Invisible/Flush, Barn, Pocket, Ghost-Sliding, Shaker, Arched, and Bathroom series. Every style imaginable.",
    img: "/viewlux/doors/cat-interior.jpg",
    Icon: Sparkles,
  },
];

const INTERIOR_SERIES = [
  ["PAD", "Painted"],
  ["SLD", "Slat"],
  ["CAD", "Carved"],
  ["HGD", "High Gloss"],
  ["FLD", "Flat"],
  ["IND", "Invisible / Flush"],
  ["SBD", "Sliding Barn"],
  ["POD", "Pocket"],
  ["GHD", "Ghost / Hidden Slide"],
  ["SHD", "Shaker"],
  ["ARD", "Arched"],
  ["BAD", "Bathroom"],
];

const CUSTOM_OPTIONS = [
  { t: "Size", b: "Standard to oversized — built to your rough opening." },
  { t: "Finish", b: "Powder-coat, anodized, painted, wood veneer, high-gloss." },
  { t: "Hardware", b: "Pull handles, electronic locks, FritsJurgens pivot, smart access." },
  { t: "Glass", b: "Clear, frosted, tempered LoĒ², dual-pane insulated." },
  { t: "Configuration", b: "Single, double, sidelights, transom, pocket, pivot, barn slide." },
  { t: "Core", b: "Solid core, semi-solid core, aluminum honeycomb, chipboard." },
];

export default function CustomDoorsPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  return (
    <div data-testid="doors-page" className="bg-cream pb-16 md:pb-0 font-nunito">
      <SeoHead
        title="Custom Aluminum & Wood Doors — ViewLux Colorado | 120+ Designs"
        description="120+ custom door designs across cast aluminum, wood, stainless steel, and interior series. Built-to-order from Frederick, CO. Pivot, pocket, barn, French, bifold."
        path="/doors"
      />
      <BreadcrumbSchema
        testId="doors"
        items={[
          { name: "Home", url: "https://viewluxcolorado.com/" },
          { name: "Custom Doors", url: "https://viewluxcolorado.com/doors" },
        ]}
      />
      <UtilityBar />
      <Header
        onRequestQuote={() => setQuoteOpen(true)}
        onDownloadCatalog={() => setCatalogOpen(true)}
      />

      {/* HERO */}
      <section data-testid="doors-hero" className="relative bg-[#0f2420] text-white overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImg
            src="/viewlux/doors/doors-hero.jpg"
            alt="Custom matte black aluminum entrance door"
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f2420] via-[#0f2420]/70 to-[#0f2420]/30" />
        </div>
        <div className="relative container-vlx pt-44 md:pt-52 pb-24 md:pb-36">
          <div className="max-w-3xl">
            <span className="eyebrow eyebrow-light">The Doors Collection</span>
            <div className="mt-4 rule-gold" />
            <h1 className="heading-lg mt-8 text-[42px] md:text-[72px] leading-[1.02]">
              Custom Doors.{" "}
              <em className="italic font-light text-[#e2b880]">Made for you.</em>
            </h1>
            <p className="mt-8 text-white/75 text-[15.5px] md:text-[17px] leading-[1.85] max-w-xl">
              Over 120 designs across cast aluminum, wood, and stainless steel.
              Every door is built to your exact opening, in your finish, with
              your hardware. Factory-direct from the ViewLux line.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="doors-cta-quote"
                onClick={() => setQuoteOpen(true)}
                className="btn-pill btn-primary"
              >
                Request a Quote
              </button>
              <button
                onClick={() => setCatalogOpen(true)}
                data-testid="doors-cta-pdf"
                className="btn-pill btn-ghost-light inline-flex items-center gap-2"
              >
                <FileDown className="w-4 h-4" /> The 2026 Doors Catalog
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section data-testid="doors-categories" id="categories" className="bg-cream">
        <div className="container-vlx py-24 md:py-32">
          <div className="max-w-2xl">
            <span className="eyebrow">Four Material Stories</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[36px] md:text-[54px] leading-[1.05] text-[#0f2420]">
              Choose your{" "}
              <em className="italic font-light text-[#7a5b2f]">material.</em>{" "}
              Pick your design. We&rsquo;ll build the rest.
            </h2>
          </div>

          <ul className="mt-16 md:mt-24 grid md:grid-cols-2 gap-px bg-[#c99658]/25">
            {CATEGORIES.map((c, i) => (
              <li
                key={c.code}
                data-testid={`doors-category-${i}`}
                className="bg-cream group flex flex-col"
              >
                <div className="relative aspect-[4/3] bg-[#0f2420] overflow-hidden">
                  <ResponsiveImg
                    src={c.img}
                    alt={c.title}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2200ms] ease-out group-hover:scale-[1.04]"
                  />
                  <span className="absolute top-5 left-5 text-[10px] tracking-[0.4em] uppercase font-raleway text-[#e2b880] bg-[#0f2420]/70 backdrop-blur px-3 py-1.5">
                    {c.code}
                  </span>
                  <span className="absolute bottom-5 left-5 text-[10.5px] tracking-[0.35em] uppercase font-raleway text-white bg-[#c99658]/90 px-3 py-1.5">
                    {c.count}
                  </span>
                </div>
                <div className="p-7 md:p-10">
                  <div className="flex items-center gap-3 text-[#7a5b2f]">
                    <c.Icon className="w-4 h-4" strokeWidth={1.6} />
                    <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="heading-lg mt-4 text-[26px] md:text-[30px] text-[#0f2420] leading-[1.15]">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-neutral-700 text-[14px] leading-[1.85] max-w-md">
                    {c.desc}
                  </p>
                  <button
                    onClick={() => setQuoteOpen(true)}
                    data-testid={`doors-category-cta-${i}`}
                    className="link-arrow mt-6"
                    aria-label={`Spec a door from the ${c.title} series`}
                  >
                    Spec a Door
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* INTERIOR SERIES INDEX */}
      <section className="bg-white text-[#0f2420] border-y border-[#c99658]/15">
        <div className="container-vlx py-24 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-5">
              <span className="eyebrow">Interior Series</span>
              <div className="mt-4 rule-gold" />
              <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] leading-[1.1]">
                Twelve interior series.{" "}
                <em className="italic font-light text-[#7a5b2f]">
                  Seventy+ designs.
                </em>
              </h2>
              <p className="mt-6 text-neutral-700 text-[14.5px] leading-[1.9] max-w-md">
                Every style from invisible flush doors that disappear into the
                wall to dramatic carved hardwood statement pieces. Pull the
                catalog for the full library.
              </p>
            </div>
            <ul className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#c99658]/25">
              {INTERIOR_SERIES.map(([code, name]) => (
                <li
                  key={code}
                  className="bg-white p-5 md:p-6 hover:bg-cream transition-colors"
                >
                  <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#7a5b2f]">
                    {code}
                  </span>
                  <div className="mt-2 font-cormorant italic text-[#0f2420] text-[18px] md:text-[20px] leading-[1.2]">
                    {name}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CUSTOM WITHOUT LIMITS */}
      <section className="bg-[#0f2420] text-white">
        <div className="container-vlx py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="eyebrow eyebrow-light">Made-to-Order</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[34px] md:text-[52px] leading-[1.05] text-white">
              Pick a design.{" "}
              <em className="italic font-light text-[#e2b880]">
                We customize every detail.
              </em>
            </h2>
            <p className="mt-6 text-white/65 text-[14.5px] leading-[1.9] max-w-xl">
              Each design in the catalog is just the starting point. Every
              parameter below is fully configurable on every door we build.
            </p>
          </div>
          <ul className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#c99658]/15">
            {CUSTOM_OPTIONS.map((o, i) => (
              <li
                key={o.t}
                data-testid={`doors-option-${i}`}
                className="bg-[#0f2420] p-7 md:p-9 flex items-start gap-4"
              >
                <span className="w-9 h-9 mt-0.5 inline-flex items-center justify-center rounded-full bg-[#c99658] text-[#0f2420] flex-shrink-0">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </span>
                <div>
                  <h3 className="font-cormorant text-[22px] text-[#e2b880]">{o.t}</h3>
                  <p className="mt-2 text-white/65 text-[13.5px] leading-[1.75]">{o.b}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SEO LONG-FORM + FAQ */}
      <section className="bg-white border-t border-[#c99658]/15">
        <div className="container-vlx py-20 md:py-28 max-w-4xl">
          <span className="eyebrow">The Doors Guide</span>
          <div className="mt-4 rule-gold" />
          <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] text-[#0f2420] leading-[1.12]">
            Custom aluminum, wood, and steel doors{" "}
            <em className="italic font-light text-[#7a5b2f]">
              engineered for Colorado homes.
            </em>
          </h2>
          <div className="mt-8 space-y-5 text-[15px] leading-[1.95] text-neutral-700 font-nunito">
            <p>
              ViewLux Colorado manufactures over 120 door designs across four
              material families: <strong>cast aluminum entry doors</strong>{" "}
              (powder-coated, ribbed, slatted, paneled, gold-accented, even
              LED-lit), <strong>wood entry doors</strong> in natural veneer,
              painted, and dyed-paper finishes with solid or semi-solid cores,
              <strong> stainless-steel doors</strong> with aluminum honeycomb
              cores in brushed or polished finishes, and a complete{" "}
              <strong>interior door library</strong> spanning twelve series —
              painted, slat, carved, high-gloss, flat, invisible flush, sliding
              barn, pocket, ghost slide, shaker, arched, and bathroom.
            </p>
            <p>
              Every door is built-to-order to your exact rough opening.
              Configurable parameters include size (standard to oversized),
              finish (powder-coat, anodized, painted, wood veneer, high-gloss),
              hardware (FritsJurgens pivot, Roto Frank multipoint, electronic
              smart locks, integrated handles), glass (clear, frosted, tempered
              LoĒ² dual-pane insulated), configuration (single, double,
              sidelights, transom, pocket, pivot, barn slide), and core (solid,
              semi-solid, honeycomb, chipboard). All exterior doors carry the
              same <strong>NFRC</strong> certified performance ratings and{" "}
              <strong>AAMA Gold Label</strong> compliance as our window line.
            </p>
            <p>
              ViewLux ships factory-direct from Frederick, Colorado, which means
              no dealer markup, faster lead times for Front-Range and Western
              Slope projects, and direct engineering support during specification.
              Architects can request CAD/Revit profiles, U-factor and SHGC data
              sheets, and shop drawings as part of the quote.
            </p>
          </div>
        </div>
      </section>

      <FaqSection
        testId="doors-faq"
        eyebrow="Frequently Asked"
        title={
          <>
            What architects and homeowners{" "}
            <em className="italic font-light text-[#7a5b2f]">ask us</em> about
            custom doors.
          </>
        }
        intro="Quick answers for the questions we get most often during the spec phase. Need something more specific? Send us your opening — we'll quote it the same day."
        items={[
          {
            q: "What materials do you build custom doors in?",
            a: "We manufacture in four material families: cast aluminum, solid and engineered wood, stainless steel with aluminum honeycomb core, and interior-grade composite and MDF for our 70+ interior designs. Each material can be paired with any of our 12+ finish systems.",
          },
          {
            q: "Are your exterior doors energy-efficient enough for Colorado winters?",
            a: "Yes. Every ViewLux exterior door uses a thermally broken aluminum or insulated wood frame, tempered LoĒ² dual-pane insulated glass, and continuous weather-stripping. They carry NFRC certified U-factor and SHGC ratings and the AAMA Gold Label — the same performance class as our window systems, rated for Climate Zone 5 and 6 (which covers most of Colorado, Wyoming, and Utah).",
          },
          {
            q: "Can you build oversized or pivot doors?",
            a: "Yes. We routinely build pivot doors up to 60\" wide and 10' tall, single-leaf entrance doors to 48\" × 108\", and double-door configurations beyond 96\" overall width. Pivot pivots are hardware-rated FritsJurgens systems. Heavier units may require structural framing review on site.",
          },
          {
            q: "What is the typical lead time?",
            a: "Standard catalog designs in stock finishes ship 6–8 weeks from quote approval. Custom finishes, oversized openings, or stainless and high-gloss families typically run 10–12 weeks. Rush production is available on aluminum doors when timeline is critical.",
          },
          {
            q: "Do you provide installation or just supply the doors?",
            a: "We ship factory-direct to your jobsite. Installation is handled either by your own framing/finish carpentry crew or by one of our certified Colorado installers — we'll connect you with one when you request a quote. We also provide shop drawings, mounting templates, and direct phone support during install.",
          },
          {
            q: "Can I see and touch a sample before specifying?",
            a: "Yes. We can ship finish samples and hardware swatches to your studio at no cost, and our showroom in Frederick, Colorado has full-size operable doors from each material family. Book a visit through the Request a Quote form.",
          },
        ]}
      />

      {/* CTA strip */}
      <section className="bg-cream border-t border-[#c99658]/15">
        <div className="container-vlx py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <span className="eyebrow">Specifying a Door?</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] text-[#0f2420] leading-[1.1]">
              Send us the opening.{" "}
              <em className="italic font-light text-[#7a5b2f]">
                We&rsquo;ll quote it the same day.
              </em>
            </h2>
            <p className="mt-6 text-neutral-700 text-[14.5px] leading-[1.9] max-w-md">
              Tell us the rough opening dimensions, the design code from the
              catalog, and your finish preference &mdash; we&rsquo;ll respond
              with pricing and shop drawings.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                data-testid="doors-bottom-quote"
                className="btn-pill btn-primary"
              >
                Request a Quote <ArrowRight className="w-4 h-4 ml-1" />
              </button>
              <button
                onClick={() => setCatalogOpen(true)}
                data-testid="doors-bottom-pdf"
                className="btn-pill btn-ghost inline-flex items-center gap-2"
              >
                <FileDown className="w-4 h-4" /> Doors Catalog · PDF
              </button>
            </div>
          </div>
          <div className="md:col-span-5">
            <Link
              to="/commercial"
              className="block bg-[#0f2420] text-white p-7 md:p-9 group transition-colors hover:bg-[#16332d]"
              data-testid="doors-commercial-link"
            >
              <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#e2b880]">
                Commercial Project?
              </span>
              <h3 className="font-cormorant italic text-white text-[26px] md:text-[30px] mt-3 leading-[1.2]">
                Storefront, curtain wall &amp; entrance systems.
              </h3>
              <span className="link-arrow !text-[#e2b880] mt-6 inline-flex group-hover:!text-white">
                Visit Commercial <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer onDownloadCatalog={() => setCatalogOpen(true)} />

      <QuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      <CatalogDialog open={catalogOpen} onClose={() => setCatalogOpen(false)} kind="doors" />
      <AccessibilityPanel />
      <MobileStickyBar onRequestQuote={() => setQuoteOpen(true)} />
    </div>
  );
}
